import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsDatabaseFill, BsArrowUpRightCircleFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Button, CircularProgress } from "@nextui-org/react";
import { subMonths, subDays, format } from "date-fns";
import { MdOutlineCalendarMonth, MdOutlineStarBorder } from "react-icons/md";

import PaginationTable from "../../components/PaginationTable";
import ACircleUserDisplay from "../../components/Kit/ACircleUserDisplay";
import Modal from "../../components/Modal";
import OverviewCard from "../../components/OverviewCard";
import LineChart from "../../components/charts/LineChart";
import CalendarDropdown from "../../components/CalendarDropdown";

import { financial_details_table_columns } from "../../constants/tableHeaders";
import { getGradeBackgroundColor } from "../../utils/colorUtils";
import { useAppContext } from "../../contexts/AppContext";
import { ArrowTopRightIcon } from "../../assets/icons/audio-icons";
import { getFinancialInsightsSales } from "../../redux/actions/financialInsightsActions";
import {
  getFinancialDetailOverviewData,
  searchFinancialSales,
} from "../../utils/financialInsightsUtils";
import {
  getOperatorSalesInfo,
  initFinancialDetail,
} from "../../redux/actions/financialDetailActions";
import { filterSortAndPaginateFinancialDetails } from "../../utils/searchOperators";

const FinancialDetails = () => {
  const { t } = useTranslation();
  const { searchText, financialDateRange } = useAppContext();
  const { sales, loading_sales } = useSelector(
    (state) => state.financial_insights.financial_insights
  );
  const { operator_id } = useSelector((state) => state.financial_detail.financial_detail);
  const [filteredSales, setFilteredSales] = useState([]);
  const [openOperatorSales, setOpenOperatorSales] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const dispatch = useDispatch();
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [filteredOperatorsCount, setFilteredOperatorsCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  const [sortDescriptor, setSortDescriptor] = useState({
    sortBy: "name",
    direction: "desc",
  });

  useEffect(() => {
    const { operators: filteredOperators, nbFiltered: nbFilteredOperators } =
      filterSortAndPaginateFinancialDetails(
        filteredSales,
        searchText,
        sortDescriptor,
        rowsPerPage,
        page - 1
      );

    setFilteredOperators(filteredOperators);
    setFilteredOperatorsCount(nbFilteredOperators);
  }, [filteredSales, searchText, sortDescriptor, rowsPerPage, page]);

  useEffect(() => {
    // const start = "2023-09-10T00:00:00+02:00";
    // const end = "2023-09-15T00:00:00+02:00";
    // if (sales.length === 0) {
    dispatch(
      getFinancialInsightsSales({
        start_date: financialDateRange.startDate,
        end_date: financialDateRange.endDate,
      })
    );
    // }
  }, [financialDateRange]);

  useEffect(() => {
    const filtered_sales = searchFinancialSales(sales, searchText);
    setFilteredSales(filtered_sales);
  }, [sales, searchText]);

  const tableEmptyContent = () => {
    return (
      <div className="w-full h-full flex-1 flex flex-col items-center justify-center bg-white rounded-md py-12">
        <BsDatabaseFill className="text-gray-300 w-12 h-12" />
        <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
      </div>
    );
  };

  const renderCell = (item, columnKey) => {
    const cellValue = item[columnKey];
    switch (columnKey) {
      case "name":
        return (
          <ACircleUserDisplay
            firstname={item.name}
            lastname=""
            email=""
          />
        );
      case "outgoing_calls_count":
        return (
          <div className="text-sm text-[#404040]/[0.6] w-full flex justify-start items-center">
            {item?.outgoing_calls_count === 0
              ? item?.outgoing_calls_count
              : item?.outgoing_calls_count.toFixed(0)}
          </div>
        );
      case "revenue":
        return (
          <div className="w-full flex justify-start items-center">
            <div className="flex justify-start items-center  font-semibold text-center px-[8px] py-[4px] rounded-md text-xs bg-grade-high/[0.1] text-grade-high">
              <span className="mx-[4px]">{`€ ${
                item?.revenue === 0
                  ? item?.revenue
                  : (item?.revenue || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })
              }`}</span>
            </div>
          </div>
        );
      case "actions":
        return (
          <div className="w-full h-full flex items-center justify-end">
            <div className="flex items-center justify-center bg-[#4318FF] rounded-md p-[10px]">
              <ArrowTopRightIcon
                size={8}
                className=" text-primary"
              />
            </div>
          </div>
        );
      default:
        return cellValue;
    }
  };

  const handleOnRowAction = (item) => async () => {
    if (item) {
      if (item.id !== operator_id) {
        await dispatch(initFinancialDetail());
      }
      setSelectedItem(item);
      setOpenOperatorSales(true);
    }
  };

  return (
    <div className="p-6 pb-32">
      <PaginationTable
        items={filteredOperators}
        columns={financial_details_table_columns}
        emptyState={tableEmptyContent()}
        renderCell={renderCell}
        onRowAction={handleOnRowAction}
        sortDescriptor={sortDescriptor}
        setSortDescriptor={setSortDescriptor}
        isLoading={loading_sales}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        displaySelectAll={false}
        itemNaming="representatives"
        overrideTotalItems={filteredOperatorsCount}
      />
      {openOperatorSales && (
        <Modal
          isOpen={openOperatorSales}
          showFooter={false}
          size="4xl"
          handleOnClose={() => setOpenOperatorSales(false)}
        >
          <SaleDetailsModalContent
            item={selectedItem}
            handleOnClose={() => setOpenOperatorSales(false)}
          />
        </Modal>
      )}
    </div>
  );
};

const SaleDetailsModalContent = ({ item, handleOnClose = () => {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // const { operators, loading } = useSelector((state) => state.operators.operators);
  const { calls } = useSelector((state) => state.analytics.analytics);
  const { operator_sales, operator_sales_kpis, operator_sales_grade, loading } = useSelector(
    (state) => state.financial_detail.financial_detail
  );
  const { financialDateRange } = useAppContext();
  const [operatorSalesOverviews, setOperatorSalesOverviews] = useState({});
  const [currentDateRange, setCurrentDateRange] = useState(financialDateRange);

  useEffect(() => {
    // DONE: Fetch call from api
    if (operator_sales_kpis) {
      const overview_data = getFinancialDetailOverviewData(calls, operator_sales_kpis);
      setOperatorSalesOverviews(overview_data);
    }
  }, [operator_sales_kpis, calls]);

  useEffect(() => {
    // DONE: Fetch call from api
    if (item) {
      dispatch(getOperatorSalesInfo({ operator: item, ...currentDateRange }));
    }
  }, [item, currentDateRange]);

  const getLineChartData = () => {
    const data = {};
    operator_sales.forEach((operator_sale) => {
      const date = format(new Date(operator_sale.date), "yyyy-MM-dd");
      if (!data?.date) {
        data[date] = operator_sale.revenue;
      } else {
        data[date] += operator_sale.revenue;
      }
    });
    return data;
  };

  const operatorGrade = Math.round((operator_sales_grade + Number.EPSILON) * 10) / 10 || 0;
  const gradeBgColor = getGradeBackgroundColor(operatorGrade);

  const calendarDefinedRanges = [
    {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
      label: t("Last 7 days"),
    },
    {
      startDate: subMonths(new Date(), 3),
      endDate: new Date(),
      label: t("Last 3 months"),
    },
    {
      startDate: subMonths(new Date(), 6),
      endDate: new Date(),
      label: t("Last 6 months"),
    },
  ];

  return (
    <div className="flex flex-col space-y-12 w-full bg-background px-8 pt-8 pb-6">
      <div className="flex justify-between space-x-6 w-full">
        <div className="flex flex-col items-start w-2/5 h-full">
          <div className="flex items-end space-x-4 justify-between w-full">
            <h1 className="text-3xl text-primary font-semibold capitalize">{item?.name}</h1>
          </div>
          <div className="flex flex-col space-y-0,5 items-start text-gray-500">
            <span className="text-sm">{item?.team}</span>
            <span className="text-sm">{item?.department || "Department"}</span>
            <div
              className={`flex justify-start items-center font-normal text-center px-[8px] py-[4px] rounded-md uppercase text-xs mt-[8px] ${gradeBgColor}`}
            >
              <MdOutlineStarBorder size={20} />
              <span className="mx-[4px]">{operator_sales_grade?.toFixed(1) || 0}</span>
            </div>
          </div>
          {/* <div className="bg-white rounded-md mt-8">
            <CalendarDropdown
              showShortcuts={false}
              dateRangeValue={currentDateRange}
              onDateRangeChange={(dates) => {
                if (dates?.endDate) {
                  setCurrentDateRange(dates);
                }
              }}
            />
          </div> */}
          <div className="flex flex-col items-start w-full mt-3">
            {calendarDefinedRanges?.map((definedRange) => (
              <div
                aria-hidden
                onClick={() => {
                  setCurrentDateRange({
                    startDate: definedRange.startDate,
                    endDate: definedRange.endDate,
                  });
                }}
                onKeyDown={() => {
                  setCurrentDateRange({
                    startDate: definedRange.startDate,
                    endDate: definedRange.endDate,
                  });
                }}
                className="cursor-pointer flex items-center justify-between bg-white space-x-2 py-[4px] px-[8px] mb-3 rounded-md border-1 border-[#E5E5E5] border-solid text-[#404040]"
              >
                <MdOutlineCalendarMonth
                  size={18}
                  color="#404040"
                />
                <span className="whitespace-nowrap text-sm">{definedRange.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="w-3/5 min-h-full">
          {loading ? (
            <div className="w-full min-h-full flex items-center justify-center">
              <CircularProgress
                aria-label="Loading..."
                color="primary"
                label={t("Loading...")}
              />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[8px] w-full h-auto">
                {Object.values(operatorSalesOverviews).map((overview) => (
                  <div
                    key={overview.description}
                    className="col-span-1"
                  >
                    <OverviewCard
                      overview={overview}
                      loading={loading}
                    />
                  </div>
                ))}
              </div>
              <div className="flex mt-[8px] bg-white rounded-md pl-[30px] pt-[28px] pb-[12px] pr-[12px]">
                <div className="flex flex-col">
                  <div className="flex items-center w-full space-x-2 mr-[24px]">
                    <span className="text-xs text-[#737373] font-semibold whitespace-nowrap">
                      {t("Sales")} / {t("day")}
                    </span>
                    {/* <IoMdArrowDropup className="w-4 h-4 text-success" /> */}
                    {/* <span className=" text-success text-[12px] leading-3 font-medium">+2.45</span> */}
                  </div>
                  {/* <div className="text-success flex items-center space-x-2 mt-1">
                      <IoIosCheckmarkCircle />
                      <span className="text-xs font-semibold">{t("On track")}</span>
                    </div> */}
                </div>
                <div className="flex-1">
                  {operator_sales.length > 0 ? (
                    <LineChart
                      data={getLineChartData()}
                      type="operator_sales"
                      minMaxAxisDiffFactor={0.25}
                      title="Sales"
                    />
                  ) : (
                    <div className="w-full h-full flex-1 flex flex-col items-center justify-center">
                      <BsDatabaseFill className="text-gray-300 w-12 h-12" />
                      <h3 className="text-sm text-gray-400 mt-3">{t("No records to display")}</h3>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialDetails;
